import React, { useState, useId } from "react";
import { Pencil, Trash2 } from "lucide-react";
import ExtraServiceEditRow from "./ExtraServiceEditRow";
import "../css/ExtraServicesEditor.css";

const SUGGESTED_SERVICE_NAMES = [
  "Additional breakfast",
  "Extra bed",
  "City tax",
  "Additional night",
  "Early check-in",
  "Late check-out",
  "Airport transfer",
  "Parking",
  "Wi-Fi",
  "Minibar",
  "Laundry",
  "Baby cot",
];

const emptyDraft = (nights) => ({
  name: "",
  price: "",
  allocationType: "required",
  nightKeys: nights ? nights.map((n) => n.key) : [],
});

/**
 * Row editor for a booking's extra_services list. Purely local-state —
 * parent owns `services` and receives the full updated array via onChange;
 * callers that persist immediately (BookingCellMenu) vs. on form submit
 * (AllocationModal, ParticipantBookingPanel) both just react to onChange.
 *
 * Uses plain Bootstrap form-control / form-select / btn primitives to match
 * the rest of the booking form (Allocation Type, Price fields) rather than
 * custom controls.
 *
 * `nights`, when passed (create-mode multi-night bookings only), renders a
 * night-toggle-chip row under the draft row so a service can apply to any
 * subset of the real dates being booked. Omit it for single-night / edit-mode
 * uniform-apply usage. At least one night must stay selected — otherwise the
 * service would be scoped to zero bookings and silently disappear on save.
 *
 * `variant="menu"` renders the flat layout used inside BookingCellMenu: a
 * header with count + subtotal, icon remove buttons and quick-add chips that
 * fill the name (price is still entered by hand).
 *
 * `totalNights`, when passed instead of `nights` (edit-mode multi-night
 * bookings — no interactive picker, uniform-apply on save), only enables the
 * "X of Y nights" sub-label on existing items that don't cover every night in
 * the stay. Without it, a service scoped to some-but-not-all nights would
 * render identically to one covering every night, with no way to tell them
 * apart before saving flattens the difference.
 */
const QUICK_ADD_COUNT = 4;

const ExtraServicesEditor = ({ services, onChange, nights, totalNights, variant }) => {
  const isMenu = variant === "menu";
  const subtotal = services.reduce((sum, s) => sum + (Number(s.price) || 0), 0);
  const datalistId = useId();
  const [draft, setDraft] = useState(() => emptyDraft(nights));
  const [editingIndex, setEditingIndex] = useState(null);
  const nightsCount = nights ? nights.length : totalNights;

  const hasValidNightSelection = !nights || draft.nightKeys.length > 0;
  const isValid =
    draft.name.trim() &&
    draft.price !== "" &&
    !Number.isNaN(Number(draft.price)) &&
    Number(draft.price) >= 0 &&
    hasValidNightSelection;

  const toggleNight = (key) => {
    setDraft((d) => ({
      ...d,
      nightKeys: d.nightKeys.includes(key) ? d.nightKeys.filter((k) => k !== key) : [...d.nightKeys, key],
    }));
  };

  const commitDraft = () => {
    if (!isValid) return;
    const entry = {
      service_name: draft.name.trim(),
      price: Number(draft.price),
      allocation_type: draft.allocationType,
    };
    if (nights) entry.date_keys = draft.nightKeys;
    onChange([...services, entry]);
    setDraft(emptyDraft(nights));
  };

  const handleRemove = (index) => {
    setEditingIndex(null);
    onChange(services.filter((_, i) => i !== index));
  };

  // Merge only the edited fields so other keys on the service (date_keys, ...) survive.
  const handleSaveEdit = (index, changes) => {
    setEditingIndex(null);
    onChange(services.map((s, i) => (i === index ? { ...s, ...changes } : s)));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitDraft();
    }
  };

  return (
    <div className={`extra-services-editor${isMenu ? " extra-services-editor--menu" : ""}`}>
      {isMenu && (
        <div className="extra-services-editor__header">
          <span className="extra-services-editor__title">Additional services</span>
          <span className="extra-services-editor__count">{services.length}</span>
          <span className="extra-services-editor__subtotal">${subtotal.toFixed(2)}</span>
        </div>
      )}
      {services.length > 0 && (
        <>
          {!isMenu && (
            <div className="extra-services-editor__row extra-services-editor__row--head" aria-hidden="true">
              <span>Service</span>
              <span>Type</span>
              <span>Price</span>
              <span />
            </div>
          )}
          {services.map((s, i) => editingIndex === i ? (
            <ExtraServiceEditRow
              key={i}
              service={s}
              suggestions={SUGGESTED_SERVICE_NAMES}
              onSave={(changes) => handleSaveEdit(i, changes)}
              onCancel={() => setEditingIndex(null)}
            />
          ) : (
            <div className="extra-services-editor__row extra-services-editor__row--item" key={i}>
              <div className="extra-services-editor__name">
                {s.service_name}
                {nightsCount && s.date_keys && s.date_keys.length !== nightsCount && (
                  <span className="extra-services-editor__sub">
                    {s.date_keys.length} of {nightsCount} nights
                  </span>
                )}
              </div>
              <div className="extra-services-editor__type">
                {s.allocation_type === "private" ? "Private" : "Required"}
              </div>
              <div className="extra-services-editor__price">${Number(s.price).toFixed(2)}</div>
              <div className="extra-services-editor__actions">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm extra-services-editor__edit-btn"
                  aria-label={`Edit ${s.service_name}`}
                  onClick={() => setEditingIndex(i)}
                >
                  <Pencil size={16} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm extra-services-editor__remove-btn"
                  aria-label={`Remove ${s.service_name}`}
                  onClick={() => handleRemove(i)}
                >
                  {isMenu ? <Trash2 size={16} aria-hidden="true" /> : "Remove"}
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {isMenu && (
        <div className="extra-services-editor__chips">
          {SUGGESTED_SERVICE_NAMES.slice(0, QUICK_ADD_COUNT).map((label) => (
            <button
              type="button"
              key={label}
              className="extra-services-editor__chip"
              onClick={() => setDraft((d) => ({ ...d, name: label }))}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="extra-services-editor__row extra-services-editor__row--draft">
        <div className="extra-services-editor__field">
          <input
            type="text"
            className="form-control"
            list={datalistId}
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            onKeyDown={handleKeyDown}
            placeholder="Service name"
            aria-label="New service name"
          />
          <datalist id={datalistId}>
            {SUGGESTED_SERVICE_NAMES.map((s) => (
              <option value={s} key={s} />
            ))}
          </datalist>
        </div>

        <div className="extra-services-editor__field">
          <select
            className="form-select"
            value={draft.allocationType}
            onChange={(e) => setDraft((d) => ({ ...d, allocationType: e.target.value }))}
            aria-label="New service type"
          >
            <option value="required">Required</option>
            <option value="private">Private</option>
          </select>
        </div>

        <div className="extra-services-editor__field extra-services-editor__price-field">
          <span className="extra-services-editor__price-prefix">$</span>
          <input
            type="number"
            min={0}
            className="form-control"
            value={draft.price}
            onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
            onKeyDown={handleKeyDown}
            placeholder="0.00"
            aria-label="New service price"
          />
        </div>

        <button
          type="button"
          className="btn btn-warning extra-services-editor__add-btn"
          disabled={!isValid}
          onClick={commitDraft}
        >
          Add
        </button>
      </div>

      {nights && (
        <div className="extra-services-editor__nights">
          <div className="extra-services-editor__night-picker">
            {nights.map((n) => (
              <button
                type="button"
                key={n.key}
                className={
                  "extra-services-editor__night-chip" + (draft.nightKeys.includes(n.key) ? " is-on" : "")
                }
                onClick={() => toggleNight(n.key)}
              >
                {n.label}
              </button>
            ))}
          </div>
          <span className="extra-services-editor__night-hint">
            {draft.nightKeys.length === 0
              ? "Select at least one night to apply this service"
              : draft.nightKeys.length === nights.length
              ? `Applies to all ${nights.length} nights — click a night to exclude it`
              : `Applies to ${draft.nightKeys.length} of ${nights.length} nights`}
          </span>
        </div>
      )}
    </div>
  );
};

export default ExtraServicesEditor;
